import React, { useState, useEffect } from 'react';
import { FiX, FiAlertTriangle } from 'react-icons/fi';
import { subscriptionsAPI } from '../../services/api';

const CancelSubscriptionModal = ({ isOpen, onClose, establishment, subscription, onSuccess }) => {
  const [formData, setFormData] = useState({
    immediately: true,
    reason: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form cuando se abre el modal
      setFormData({
        immediately: true,
        reason: ''
      });
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await subscriptionsAPI.cancelSubscription(subscription.id, formData);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cancelar la suscripción');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      immediately: true,
      reason: ''
    });
    setError(null);
    onClose();
  };

  if (!isOpen || !subscription) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Cancelar Suscripción
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {establishment?.name}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Advertencia */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <FiAlertTriangle className="h-5 w-5 text-yellow-400 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800">
                  Atención
                </h4>
                <p className="text-sm text-yellow-700 mt-1">
                  Esta acción cancelará la suscripción del establecimiento de forma inmediata.
                  El establecimiento perderá acceso a las funcionalidades del plan.
                </p>
              </div>
            </div>
          </div>

          {/* Información de la suscripción */}
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4 space-y-2">
            <h4 className="text-sm font-medium text-gray-900 mb-2">
              Detalles de la Suscripción
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Plan:</span>
                <span className="font-medium text-gray-900 capitalize">{subscription.plan}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Período:</span>
                <span className="font-medium text-gray-900">
                  {subscription.period === 'monthly' ? 'Mensual' : 'Anual'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Estado:</span>
                <span className="font-medium text-gray-900 capitalize">
                  {subscription.status === 'trial' ? 'Período de Prueba' : 'Activa'}
                </span>
              </div>
              {subscription.trial_end && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fin del Trial:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(subscription.trial_end).toLocaleDateString('es-ES')}
                  </span>
                </div>
              )}
              {subscription.current_period_end && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fin del Período:</span>
                  <span className="font-medium text-gray-900">
                    {new Date(subscription.current_period_end).toLocaleDateString('es-ES')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Checkbox de cancelación inmediata */}
          <div className="flex items-start">
            <div className="flex items-center h-5">
              <input
                id="immediately"
                type="checkbox"
                checked={formData.immediately}
                onChange={(e) => setFormData({ ...formData, immediately: e.target.checked })}
                className="h-4 w-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="immediately" className="text-sm font-medium text-gray-700">
                Cancelar inmediatamente
              </label>
              <p className="text-xs text-gray-500 mt-1">
                {formData.immediately
                  ? 'La suscripción se cancelará ahora mismo y el establecimiento perderá acceso inmediatamente.'
                  : 'La suscripción se cancelará al finalizar el período actual.'}
              </p>
            </div>
          </div>

          {/* Motivo de cancelación */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de Cancelación (opcional)
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Indica el motivo de la cancelación..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Volver
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Cancelando...' : 'Confirmar Cancelación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
